import { apiBlob, apiRequest } from "./http";
import type { QueryValue } from "./http";
import type {
  AdminCustomerOut,
  AdminOrderOut,
  AdminProductOut,
  AdminStoreOut,
  AiPricingItem,
  AiPricingOut,
  AiPricingPatch,
  AiSettingsOut,
  AiSettingsPatch,
  AiUsageRankingOut,
  AiUsageRankingQuery,
  AnnouncementOut,
  AnnouncementPatch,
  AnnouncementSave,
  FaqItemOut,
  FaqItemPatch,
  FaqItemSave,
  FaqPublicOut,
  HelpDocOut,
  HelpDocPatch,
  HelpDocPublicOut,
  HelpDocSave,
  HelpDocSummaryOut,
  LandingContentOut,
  LandingContentSave,
  LandingPageOut,
  PricingCardOut,
  PricingCardsPut,
  EmailTemplateOut,
  EmailTemplatePatch,
  EmailTemplateSave,
  PaymentChannelOut,
  PaymentChannelPatch,
  PlanConfigOut,
  PlansSettingsOut,
  PlansSettingsPut,
  PlatformAdminCreate,
  PlatformAdminOut,
  PlatformAdminPatch,
  SiteSettingsOut,
  SiteSettingsPatch,
  TenantMemberAdd,
  TenantMemberOut,
  TenantMemberPatch,
  ExportCreateRequest,
  ExportCreatedOut,
  ExportOut,
  ReportExportListQuery,
  ReportMetaOut,
  ReportPreviewOut,
  ReportPreviewRequest,
  ScheduleCreateRequest,
  ScheduleOut,
  ScheduleUpdateRequest,
  ExpireResult,
  HealthData,
  LogoutData,
  LoginRequest,
  LogoutRequest,
  MeOut,
  MetaData,
  Page,
  AdminTenantOut,
  AdminStatsOut,
  AdminPaymentOut,
  AlertOut,
  AlertResolveOut,
  AuditLogOut,
  TenantStatus,
  RefundRequest,
  ConnectionTestOut,
  StoreCreate,
  StoreOut,
  StoreUpdate,
  SyncResultOut,
  SyncRunOut,
  SyncStatusOut,
  SyncTriggerRequest,
  ProfitSnapshotOut,
  ProfitSummaryOut,
  ProfitTrendOut,
  RecomputeRequest,
  RecomputeResultOut,
  SkuProfitPageOut,
  ActionCardsOut,
  SkuDetailOut,
  SkuDetailQuery,
  BulkCostUpdateRequest,
  BulkCostUpdateOut,
  ProfitQuery,
  AiBudgetSnapshot,
  AiUsageOut,
  AbandonedCheckoutOut,
  AbandonedListOut,
  AbandonedQuery,
  AudienceOut,
  BudgetUpdateRequest,
  CustomerDetailOut,
  CustomerListOut,
  CustomerQuery,
  CustomerRecomputeRequest,
  EmailDraftListQuery,
  EmailDraftOut,
  EmailDraftUpdate,
  EmailEnqueueRequest,
  EmailEnqueueResultOut,
  EmailGenerateRequest,
  SendTimeSuggestionOut,
  SendTimeSuggestionQuery,
  EmailGenerateResultOut,
  EmailMessageListQuery,
  EmailMessageOut,
  EmailSendMode,
  EmailSendRequest,
  EmailSendResultOut,
  CustomerRecomputeResultOut,
  CustomerSummaryOut,
  EraseResultOut,
  RecoveryStatus,
  AdImportOut,
  AdImportPreviewOut,
  AdsAllocationQuery,
  AdsImportListQuery,
  AdsMappingListQuery,
  AdsCampaignFlagsQuery,
  CampaignFlagOut,
  CampaignFlagUpdate,
  CampaignWeightsRequest,
  AllocationOut,
  AllocationRecomputeRequest,
  AllocationRecomputeResultOut,
  ColumnMappingRequest,
  SkuAdMappingCreate,
  SkuAdMappingOut,
  SkuAdMappingUpdate,
  CheckoutOut,
  CheckoutRequest,
  MockCompleteRequest,
  PaymentModeOut,
  PlanOut,
  PlanUpdateRequest,
  ProDemoData,
  RefreshRequest,
  RegisterData,
  RegisterRequest,
  SubscriptionOut,
  WebhookResult,
  TenantOut,
  TenantSettingsOut,
  TenantSettingsUpdate,
  PaymentStatus,
  PaymentProvider,
  TenantUpdateRequest,
  TokenPair,
  UnsubscribeInfoOut,
  UnsubscribeRequest,
  UnsubscribeResultOut,
  UserOut,
} from "./types";

export const authApi = {
  register: (body: RegisterRequest) =>
    apiRequest<RegisterData>("/auth/register", { method: "POST", body }),
  login: (body: LoginRequest) => apiRequest<TokenPair>("/auth/login", { method: "POST", body }),
  refresh: (body: RefreshRequest = { refresh_token: null }) =>
    apiRequest<TokenPair>("/auth/refresh", { method: "POST", body }),
  logout: (body: LogoutRequest = { refresh_token: null }) =>
    apiRequest<LogoutData>("/auth/logout", { method: "POST", body }),
  me: () => apiRequest<MeOut>("/auth/me"),
};

export const tenantsApi = {
  me: () => apiRequest<TenantOut>("/tenants/me"),
  update: (body: TenantUpdateRequest) =>
    apiRequest<TenantOut>("/tenants/me", { method: "PATCH", body }),
  members: () => apiRequest<UserOut[]>("/tenants/me/members"),
  member: (memberId: string) =>
    apiRequest<UserOut>(`/tenants/me/members/${encodeURIComponent(memberId)}`),
  settings: () => apiRequest<TenantSettingsOut>("/tenants/me/settings"),
  updateSettings: (body: TenantSettingsUpdate) =>
    apiRequest<TenantSettingsOut>("/tenants/me/settings", { method: "PATCH", body }),
};

export const billingApi = {
  subscription: () => apiRequest<SubscriptionOut>("/billing/subscription"),
  /** Pro-gated: free / expired-trial tenants get HTTP 402 `plan_required`. */
  proDemo: () => apiRequest<ProDemoData>("/billing/pro-demo"),
  /** 可售卖的计费档位（DB 优先、环境变量兜底）。 */
  plans: () => apiRequest<PlanOut[]>("/billing/plans"),
  /** 当前支付模式：`mock` 表示走本地沙箱适配器，不会真实扣款。 */
  paymentMode: () => apiRequest<PaymentModeOut>("/billing/payment-mode"),
  /** 创建收银台；只有 owner 能发起（member 会拿到 403）。 */
  createCheckout: (body: CheckoutRequest) =>
    apiRequest<CheckoutOut>("/billing/checkout", { method: "POST", body }),
  /** 沙箱支付完成：仅 PAYMENT_MODE=mock 可用，走真实 webhook 链路开通 Pro。 */
  mockComplete: (body: MockCompleteRequest) =>
    apiRequest<WebhookResult>("/billing/mock/complete", { method: "POST", body }),
  /** 取消订阅：已付费周期内保留权益，到期后降级。 */
  cancelSubscription: () =>
    apiRequest<SubscriptionOut>("/billing/subscription/cancel", { method: "POST" }),
};

export const storesApi = {
  list: () => apiRequest<StoreOut[]>("/stores"),
  get: (storeId: string) => apiRequest<StoreOut>(`/stores/${encodeURIComponent(storeId)}`),
  create: (body: StoreCreate) => apiRequest<StoreOut>("/stores", { method: "POST", body }),
  update: (storeId: string, body: StoreUpdate) =>
    apiRequest<StoreOut>(`/stores/${encodeURIComponent(storeId)}`, { method: "PATCH", body }),
  remove: (storeId: string) =>
    apiRequest<{ deleted: boolean; store_id: string }>(`/stores/${encodeURIComponent(storeId)}`, {
      method: "DELETE",
    }),
  /** Connection probe: HTTP 200 even when `ok === false` (the probe itself succeeded). */
  test: (storeId: string) =>
    apiRequest<ConnectionTestOut>(`/stores/${encodeURIComponent(storeId)}/test`, {
      method: "POST",
    }),
  /** Defaults to a synchronous full sync; pass `mode: "async"` to queue it. */
  sync: (storeId: string, body: Partial<SyncTriggerRequest> = {}) =>
    apiRequest<SyncResultOut>(`/stores/${encodeURIComponent(storeId)}/sync`, {
      method: "POST",
      body: { job_type: "full", reset_cursor: false, mode: "sync", ...body },
    }),
  syncRuns: (storeId: string, query?: { page?: number; page_size?: number }) =>
    apiRequest<Page<SyncRunOut>>(`/stores/${encodeURIComponent(storeId)}/sync-runs`, {
      query: query as Record<string, QueryValue>,
    }),
  syncStatus: (storeId: string) =>
    apiRequest<SyncStatusOut>(`/stores/${encodeURIComponent(storeId)}/sync-status`),
};

export const profitApi = {
  /** KPI 汇总（收入/成本/费用/退款/净利/毛利率）+ 上一周期环比。 */
  summary: (query?: ProfitQuery) =>
    apiRequest<ProfitSummaryOut>("/profit/summary", {
      query: query as Record<string, QueryValue>,
    }),
  /** SKU 级利润分页列表（排序 / 关键词筛选），合计为全量口径。 */
  sku: (query?: ProfitQuery) =>
    apiRequest<SkuProfitPageOut>("/profit/sku", { query: query as Record<string, QueryValue> }),
  /** 仪表盘行动卡片（超卖风险 / 利润归因 / 补货提示），severity 排序。 */
  actions: (
    query?: Omit<
      ProfitQuery,
      "q" | "include_ad_spend" | "sort" | "order" | "page" | "page_size" | "period_type"
    >,
  ) =>
    apiRequest<ActionCardsOut>("/profit/actions", { query: query as Record<string, QueryValue> }),
  /** SKU 利润详情：建议最低售价 + 活动级广告分摊明细。 */
  skuDetail: (query: SkuDetailQuery) =>
    apiRequest<SkuDetailOut>("/profit/sku-detail", { query: query as Record<string, QueryValue> }),
  /** 按 day / week / month 输出趋势点。 */
  trend: (query?: ProfitQuery) =>
    apiRequest<ProfitTrendOut>("/profit/trend", { query: query as Record<string, QueryValue> }),
  /** 已落库的利润快照（分页）。 */
  snapshots: (query?: ProfitQuery) =>
    apiRequest<Page<ProfitSnapshotOut>>("/profit/snapshots", {
      query: query as Record<string, QueryValue>,
    }),
  /** 手动重算（幂等；mode=async 时投递给 Arq）。owner 专属。 */
  recompute: (body: RecomputeRequest) =>
    apiRequest<RecomputeResultOut>("/profit/snapshots/recompute", { method: "POST", body }),
  /** 批量维护商品成本（COGS 输入）。 */
  bulkCosts: (body: BulkCostUpdateRequest) =>
    apiRequest<BulkCostUpdateOut>("/profit/costs/bulk", { method: "POST", body }),
};

export const customersApi = {
  /** 分页列表（含全量合计与分层计数；KPI 不随分页变化）。 */
  list: (query?: CustomerQuery) =>
    apiRequest<CustomerListOut>("/customers", { query: query as Record<string, QueryValue> }),
  /** 顶部 KPI：分层人数、复购率、平均复购天数、待唤醒敞口。 */
  summary: (query?: CustomerQuery) =>
    apiRequest<CustomerSummaryOut>("/customers/summary", {
      query: query as Record<string, QueryValue>,
    }),
  /** 待唤醒名单（等价于 segment=silent,churned）。 */
  silent: (query?: CustomerQuery) =>
    apiRequest<CustomerListOut>("/customers/silent", {
      query: query as Record<string, QueryValue>,
    }),
  /** S8 受众：只拿 customers.id，不下发 PII。 */
  audience: (query?: CustomerQuery) =>
    apiRequest<AudienceOut>("/customers/audience", {
      query: query as Record<string, QueryValue>,
    }),
  detail: (customerId: string) =>
    apiRequest<CustomerDetailOut>(`/customers/${encodeURIComponent(customerId)}`),
  /** 重算客户指标（幂等）。owner 专属；mode=async 时投递给 Arq。 */
  recompute: (body: CustomerRecomputeRequest = {}) =>
    apiRequest<CustomerRecomputeResultOut>("/customers/metrics/recompute", {
      method: "POST",
      body: { mode: "sync", scope: "full", lookback_days: 30, ...body },
    }),
  /** GDPR 擦除（伪匿名化，金额与分层口径不变）。owner 专属。 */
  erase: (customerId: string, body: { reason?: string | null } = {}) =>
    apiRequest<EraseResultOut>(`/customers/${encodeURIComponent(customerId)}/erase`, {
      method: "POST",
      body,
    }),
  /** 弃单挽回列表（KPI 不受 status / 分页影响）。 */
  abandoned: (query?: AbandonedQuery) =>
    apiRequest<AbandonedListOut>("/customers/abandoned", {
      query: query as Record<string, QueryValue>,
    }),
  /** 推进弃单挽回状态（sent / recovered / 回退 open）。 */
  updateAbandonedRecovery: (abandonedId: string, status: RecoveryStatus) =>
    apiRequest<AbandonedCheckoutOut>(
      `/customers/abandoned/${encodeURIComponent(abandonedId)}/recovery`,
      { method: "PATCH", body: { status } },
    ),
};

export const adsApi = {
  /**
   * 上传报表（multipart）并自动识别列映射，直接返回预览。
   * 解析不出数据行也返回 201：`status === "failed"` + `fatal` 说明原因。
   */
  upload: (form: FormData) =>
    apiRequest<AdImportPreviewOut>("/ads/imports", { method: "POST", body: form }),
  list: (query?: AdsImportListQuery) =>
    apiRequest<Page<AdImportOut>>("/ads/imports", { query: query as Record<string, QueryValue> }),
  get: (importId: string) =>
    apiRequest<AdImportOut>(`/ads/imports/${encodeURIComponent(importId)}`),
  /** 提交列映射后重新解析（body 可省略，按当前映射重跑一次）。 */
  preview: (importId: string, body: Partial<ColumnMappingRequest> = {}) =>
    apiRequest<AdImportPreviewOut>(`/ads/imports/${encodeURIComponent(importId)}/preview`, {
      method: "POST",
      body,
    }),
  /** 确认入库（幂等：重复确认会先清掉旧明细）。 */
  confirm: (importId: string, body: Partial<ColumnMappingRequest> = {}) =>
    apiRequest<AdImportOut>(`/ads/imports/${encodeURIComponent(importId)}/confirm`, {
      method: "POST",
      body,
    }),
  /** 作废：删明细 + 删文件。 */
  void: (importId: string) =>
    apiRequest<AdImportOut>(`/ads/imports/${encodeURIComponent(importId)}/void`, {
      method: "POST",
    }),
  /** 广告 -> SKU 映射列表。 */
  mappings: (query?: AdsMappingListQuery) =>
    apiRequest<Page<SkuAdMappingOut>>("/ads/mappings", {
      query: query as Record<string, QueryValue>,
    }),
  createMapping: (body: SkuAdMappingCreate) =>
    apiRequest<SkuAdMappingOut>("/ads/mappings", { method: "POST", body }),
  updateMapping: (mappingId: string, body: SkuAdMappingUpdate) =>
    apiRequest<SkuAdMappingOut>(`/ads/mappings/${encodeURIComponent(mappingId)}`, {
      method: "PATCH",
      body,
    }),
  deleteMapping: (mappingId: string) =>
    apiRequest<{ deleted: boolean; mapping_id: string }>(
      `/ads/mappings/${encodeURIComponent(mappingId)}`,
      { method: "DELETE" },
    ),
  /** 按活动批量设置 SKU 分摊权重（手动调整，规则持久化 = 记忆）。 */
  setCampaignWeights: (body: CampaignWeightsRequest) =>
    apiRequest<Page<SkuAdMappingOut>>("/ads/mappings/weights", { method: "PUT", body }),
  /** 活动标记列表（待暂停等）。 */
  campaignFlags: (query?: AdsCampaignFlagsQuery) =>
    apiRequest<Page<CampaignFlagOut>>("/ads/campaign-flags", {
      query: query as Record<string, QueryValue>,
    }),
  /** 标记 / 取消活动（幂等 upsert）。 */
  setCampaignFlag: (body: CampaignFlagUpdate) =>
    apiRequest<CampaignFlagOut>("/ads/campaign-flags", { method: "PUT", body }),
  /** 分摊结果（不落库；落库走 recompute）。 */
  allocation: (query?: AdsAllocationQuery) =>
    apiRequest<AllocationOut>("/ads/allocation", { query: query as Record<string, QueryValue> }),
  /** 按广告分摊口径重算利润快照（幂等）。owner 专属。 */
  recompute: (body: AllocationRecomputeRequest) =>
    apiRequest<AllocationRecomputeResultOut>("/ads/allocation/recompute", {
      method: "POST",
      body: { mode: "sync", ...body },
    }),
};

export const aiApi = {
  /** 批量生成唤醒邮件草稿（Pro）。dry_run 时只返回成本预估。 */
  generate: (body: EmailGenerateRequest) =>
    apiRequest<EmailGenerateResultOut>("/ai/emails/generate", { method: "POST", body }),

  /** 建议发送时机（按历史成交时段反推；customer_ids 定向，缺省 = 全店口径）。 */
  sendTimeSuggestion: (query?: SendTimeSuggestionQuery) =>
    apiRequest<SendTimeSuggestionOut>("/ai/send-time-suggestion", {
      query: query as Record<string, QueryValue>,
    }),

  /** 草稿列表。 */
  listDrafts: (query?: EmailDraftListQuery) =>
    apiRequest<Page<EmailDraftOut>>("/ai/drafts", { query: query as Record<string, QueryValue> }),
  getDraft: (draftId: string) =>
    apiRequest<EmailDraftOut>(`/ai/drafts/${encodeURIComponent(draftId)}`),
  /** 编辑草稿（切换标题 / 正文，或人工改写文案）。 */
  updateDraft: (draftId: string, body: EmailDraftUpdate) =>
    apiRequest<EmailDraftOut>(`/ai/drafts/${encodeURIComponent(draftId)}`, {
      method: "PATCH",
      body,
    }),
  /** 作废草稿（owner）。 */
  discardDraft: (draftId: string) =>
    apiRequest<EmailDraftOut>(`/ai/drafts/${encodeURIComponent(draftId)}/discard`, {
      method: "POST",
    }),

  /** 草稿入队（幂等）。 */
  enqueue: (body: EmailEnqueueRequest) =>
    apiRequest<EmailEnqueueResultOut>("/ai/messages", { method: "POST", body }),
  /** 触发发送（owner）。 */
  send: (body: EmailSendRequest) =>
    apiRequest<EmailSendResultOut>("/ai/messages/send", { method: "POST", body }),
  /** 发送记录列表。 */
  listMessages: (query?: EmailMessageListQuery) =>
    apiRequest<Page<EmailMessageOut>>("/ai/messages", {
      query: query as Record<string, QueryValue>,
    }),
  getMessage: (messageId: string) =>
    apiRequest<EmailMessageOut>(`/ai/messages/${encodeURIComponent(messageId)}`),
  /** 重试失败邮件（owner）。 */
  retryMessage: (messageId: string, mode: EmailSendMode = "sync") =>
    apiRequest<EmailSendResultOut>(`/ai/messages/${encodeURIComponent(messageId)}/retry`, {
      method: "POST",
      query: { mode },
    }),

  /** AI 用量与预算。 */
  usage: (query?: { page?: number; page_size?: number }) =>
    apiRequest<AiUsageOut>("/ai/usage", { query: query as Record<string, QueryValue> }),
  /** 修改月度预算（owner）。<= 0 表示不设上限。 */
  updateBudget: (body: BudgetUpdateRequest) =>
    apiRequest<AiBudgetSnapshot>("/ai/usage/budget", { method: "PATCH", body }),

  /** 退订落地页信息（公开；不写入，防邮件预加载误退订）。 */
  unsubscribeInfo: (token: string, customerId: string) =>
    // 后端用 `token` + `c` 两个查询参数，别写成 `t`
    apiRequest<UnsubscribeInfoOut>("/ai/unsubscribe", { query: { token, c: customerId } }),
  /** 确认退订（公开；幂等）。 */
  unsubscribe: (body: UnsubscribeRequest) =>
    apiRequest<UnsubscribeResultOut>("/ai/unsubscribe", { method: "POST", body }),
};

export const reportsApi = {
  /** 报表中心元信息：类型 / 列定义 / 币种 / 店铺 / 行数上限（一次拿全）。 */
  meta: () => apiRequest<ReportMetaOut>("/reports/meta"),

  /** 同步预览前 N 行（同时给出总行数，前端据此决定同步 / 异步导出）。 */
  preview: (body: ReportPreviewRequest) =>
    apiRequest<ReportPreviewOut>("/reports/preview", { method: "POST", body }),

  /** 创建导出任务。mode=sync 请求内直出；async 投给 worker。 */
  create: (body: ExportCreateRequest) =>
    apiRequest<ExportCreatedOut>("/reports/exports", { method: "POST", body }),
  list: (query?: ReportExportListQuery) =>
    apiRequest<Page<ExportOut>>("/reports/exports", { query: query as Record<string, QueryValue> }),
  get: (exportId: string) =>
    apiRequest<ExportOut>(`/reports/exports/${encodeURIComponent(exportId)}`),
  /** 失败 / 过期任务重试（owner）。 */
  retry: (exportId: string) =>
    apiRequest<ExportOut>(`/reports/exports/${encodeURIComponent(exportId)}/retry`, {
      method: "POST",
    }),
  delete: (exportId: string) =>
    apiRequest<{ deleted: boolean; export_id: string }>(
      `/reports/exports/${encodeURIComponent(exportId)}`,
      { method: "DELETE" },
    ),
  /** 带鉴权下载（浏览器用，自动带 Authorization；越权 / 过期 -> 404）。 */
  downloadUrl: (exportId: string) => `/reports/exports/${encodeURIComponent(exportId)}/download`,
  download: (exportId: string) => apiBlob(reportsDownloadPath(exportId)),

  /** 定时报表（owner）。 */
  listSchedules: () => apiRequest<ScheduleOut[]>("/reports/schedules"),
  createSchedule: (body: ScheduleCreateRequest) =>
    apiRequest<ScheduleOut>("/reports/schedules", { method: "POST", body }),
  updateSchedule: (scheduleId: string, body: ScheduleUpdateRequest) =>
    apiRequest<ScheduleOut>(`/reports/schedules/${encodeURIComponent(scheduleId)}`, {
      method: "PATCH",
      body,
    }),
  deleteSchedule: (scheduleId: string) =>
    apiRequest<{ deleted: boolean; schedule_id: string }>(
      `/reports/schedules/${encodeURIComponent(scheduleId)}`,
      { method: "DELETE" },
    ),
  runSchedule: (scheduleId: string) =>
    apiRequest<{ job_id: string | null; schedule_id: string }>(
      `/reports/schedules/${encodeURIComponent(scheduleId)}/run`,
      { method: "POST" },
    ),
};

function reportsDownloadPath(exportId: string): string {
  return `/reports/exports/${encodeURIComponent(exportId)}/download`;
}

export const systemApi = {
  health: () => apiRequest<HealthData>("/health"),
  meta: () => apiRequest<MetaData>("/meta"),
};

/** S17 官网管理（CMS）：Landing 文案 / FAQ / 帮助文档 / 定价卡片展示。 */
export const adminCmsApi = {
  // ---- Landing 文案 ----
  getLanding: () => apiRequest<LandingContentOut[]>("/admin/cms/landing"),
  /** 全量保存：提交内容即该语言的最终文案。 */
  saveLanding: (locale: string, body: LandingContentSave) =>
    apiRequest<LandingContentOut>(`/admin/cms/landing/${encodeURIComponent(locale)}`, {
      method: "PUT",
      body,
    }),

  // ---- FAQ ----
  listFaq: (query?: { locale?: string }) =>
    apiRequest<FaqItemOut[]>("/admin/cms/faq", { query: query as Record<string, QueryValue> }),
  createFaq: (body: FaqItemSave) =>
    apiRequest<FaqItemOut>("/admin/cms/faq", { method: "POST", body }),
  patchFaq: (faqId: string, body: FaqItemPatch) =>
    apiRequest<FaqItemOut>(`/admin/cms/faq/${encodeURIComponent(faqId)}`, {
      method: "PATCH",
      body,
    }),
  deleteFaq: (faqId: string) =>
    apiRequest<{ deleted: boolean }>(`/admin/cms/faq/${encodeURIComponent(faqId)}`, {
      method: "DELETE",
    }),

  // ---- 帮助文档 ----
  listHelpDocs: (query?: { locale?: string }) =>
    apiRequest<HelpDocOut[]>("/admin/cms/help-docs", {
      query: query as Record<string, QueryValue>,
    }),
  createHelpDoc: (body: HelpDocSave) =>
    apiRequest<HelpDocOut>("/admin/cms/help-docs", { method: "POST", body }),
  patchHelpDoc: (docId: string, body: HelpDocPatch) =>
    apiRequest<HelpDocOut>(`/admin/cms/help-docs/${encodeURIComponent(docId)}`, {
      method: "PATCH",
      body,
    }),
  deleteHelpDoc: (docId: string) =>
    apiRequest<{ deleted: boolean }>(`/admin/cms/help-docs/${encodeURIComponent(docId)}`, {
      method: "DELETE",
    }),

  // ---- 定价卡片展示字段（按语言维护） ----
  getPricingCards: (query?: { locale?: string }) =>
    apiRequest<PricingCardOut[]>("/admin/cms/pricing-cards", {
      query: query as Record<string, QueryValue>,
    }),
  /** 只改指定语言的展示文案，计费口径仍在「套餐与定价」里维护。 */
  putPricingCards: (body: PricingCardsPut, query?: { locale?: string }) =>
    apiRequest<PricingCardOut[]>("/admin/cms/pricing-cards", {
      method: "PUT",
      body,
      query: query as Record<string, QueryValue>,
    }),
};

/** 官网公开内容（免登录）：Landing 首屏在注册前就要渲染。 */
export const cmsApi = {
  /** 首屏一次拿全：文案 + 在售定价卡片。 */
  landing: (locale?: string) => apiRequest<LandingPageOut>("/cms/landing", { query: { locale } }),
  faq: (locale?: string) => apiRequest<FaqPublicOut[]>("/cms/faq", { query: { locale } }),
  helpDocs: (locale?: string) =>
    apiRequest<HelpDocSummaryOut[]>("/cms/help-docs", { query: { locale } }),
  helpDoc: (slug: string, locale?: string) =>
    apiRequest<HelpDocPublicOut>(`/cms/help-docs/${encodeURIComponent(slug)}`, {
      query: { locale },
    }),
};

export const adminApi = {
  listTenants: (query?: { q?: string; page?: number; page_size?: number }) =>
    apiRequest<Page<AdminTenantOut>>("/admin/tenants", {
      query: query as Record<string, QueryValue>,
    }),
  tenant: (tenantId: string) =>
    apiRequest<AdminTenantOut>(`/admin/tenants/${encodeURIComponent(tenantId)}`),
  updatePlan: (tenantId: string, body: PlanUpdateRequest) =>
    apiRequest<AdminTenantOut>(`/admin/tenants/${encodeURIComponent(tenantId)}/plan`, {
      method: "POST",
      body,
    }),
  /** 启停租户：suspended / closed 后 effective_plan 立即回落 free，租户接口 403。 */
  updateStatus: (tenantId: string, body: { status: TenantStatus; reason?: string | null }) =>
    apiRequest<AdminTenantOut>(`/admin/tenants/${encodeURIComponent(tenantId)}/status`, {
      method: "PATCH",
      body,
    }),
  expireTrials: () => apiRequest<ExpireResult>("/admin/trials/expire", { method: "POST" }),

  stats: () => apiRequest<AdminStatsOut>("/admin/stats"),
  /** AI 用量排行：租户榜 / 模型榜 / 功能榜同区间。 */
  aiUsageRanking: (query?: AiUsageRankingQuery) =>
    apiRequest<AiUsageRankingOut>("/admin/ai/usage-ranking", {
      query: query as Record<string, QueryValue>,
    }),
  alerts: (query?: {
    status?: string;
    type?: string;
    tenant_id?: string;
    page?: number;
    page_size?: number;
  }) =>
    apiRequest<Page<AlertOut>>("/admin/alerts", {
      query: query as Record<string, QueryValue>,
    }),
  resolveAlert: (alertId: string, body?: { note?: string | null }) =>
    apiRequest<AlertResolveOut>(`/admin/alerts/${encodeURIComponent(alertId)}/resolve`, {
      method: "POST",
      body: body ?? {},
    }),
  auditLogs: (query?: {
    tenant_id?: string;
    action?: string;
    entity?: string;
    page?: number;
    page_size?: number;
  }) =>
    apiRequest<Page<AuditLogOut>>("/admin/audit-logs", {
      query: query as Record<string, QueryValue>,
    }),
  listPayments: (query?: {
    tenant_id?: string;
    status?: PaymentStatus;
    provider?: PaymentProvider;
    page?: number;
    page_size?: number;
  }) =>
    apiRequest<Page<AdminPaymentOut>>("/admin/payments", {
      query: query as Record<string, QueryValue>,
    }),
  refundPayment: (paymentId: string, body: RefundRequest) =>
    apiRequest<AdminPaymentOut>(`/admin/payments/${encodeURIComponent(paymentId)}/refund`, {
      method: "POST",
      body,
    }),
};

/** S12 管理后台：平台设置 / 配置中心 / 数据中心。 */
export const adminSettingsApi = {
  // ---- 站点设置 ----
  getSite: () => apiRequest<SiteSettingsOut>("/admin/settings/site"),
  patchSite: (body: SiteSettingsPatch) =>
    apiRequest<SiteSettingsOut>("/admin/settings/site", { method: "PATCH", body }),

  // ---- 套餐与定价 ----
  getPlans: () => apiRequest<PlansSettingsOut>("/admin/settings/plans"),
  /** 全量替换语义：提交的 items 即最终生效集合。 */
  putPlans: (body: PlansSettingsPut) =>
    apiRequest<PlanConfigOut[]>("/admin/settings/plans", { method: "PUT", body }),

  // ---- 支付渠道 ----
  listPaymentChannels: () => apiRequest<PaymentChannelOut[]>("/admin/settings/payment-channels"),
  patchPaymentChannel: (provider: PaymentProvider, body: PaymentChannelPatch) =>
    apiRequest<PaymentChannelOut>(
      `/admin/settings/payment-channels/${encodeURIComponent(provider)}`,
      {
        method: "PATCH",
        body,
      },
    ),

  // ---- AI 配置 ----
  getAi: () => apiRequest<AiSettingsOut>("/admin/settings/ai"),
  patchAi: (body: AiSettingsPatch) =>
    apiRequest<AiSettingsOut>("/admin/settings/ai", { method: "PATCH", body }),
  listAiPricing: () => apiRequest<AiPricingOut[]>("/admin/settings/ai/pricing"),
  createAiPricing: (body: AiPricingItem) =>
    apiRequest<AiPricingOut>("/admin/settings/ai/pricing", { method: "POST", body }),
  patchAiPricing: (pricingId: string, body: AiPricingPatch) =>
    apiRequest<AiPricingOut>(`/admin/settings/ai/pricing/${encodeURIComponent(pricingId)}`, {
      method: "PATCH",
      body,
    }),

  // ---- 邮件模板 ----
  listEmailTemplates: (query?: { feature?: string; locale?: string }) =>
    apiRequest<EmailTemplateOut[]>("/admin/email-templates", {
      query: query as Record<string, QueryValue>,
    }),
  createEmailTemplate: (body: EmailTemplateSave) =>
    apiRequest<EmailTemplateOut>("/admin/email-templates", { method: "POST", body }),
  patchEmailTemplate: (templateId: string, body: EmailTemplatePatch) =>
    apiRequest<EmailTemplateOut>(`/admin/email-templates/${encodeURIComponent(templateId)}`, {
      method: "PATCH",
      body,
    }),
  deleteEmailTemplate: (templateId: string) =>
    apiRequest<{ deleted: boolean }>(`/admin/email-templates/${encodeURIComponent(templateId)}`, {
      method: "DELETE",
    }),

  // ---- 平台管理员 ----
  listAdmins: () => apiRequest<PlatformAdminOut[]>("/admin/admins"),
  createAdmin: (body: PlatformAdminCreate) =>
    apiRequest<PlatformAdminOut>("/admin/admins", { method: "POST", body }),
  patchAdmin: (adminId: string, body: PlatformAdminPatch) =>
    apiRequest<PlatformAdminOut>(`/admin/admins/${encodeURIComponent(adminId)}`, {
      method: "PATCH",
      body,
    }),

  // ---- 公告 ----
  listAnnouncements: () => apiRequest<AnnouncementOut[]>("/admin/announcements"),
  createAnnouncement: (body: AnnouncementSave) =>
    apiRequest<AnnouncementOut>("/admin/announcements", { method: "POST", body }),
  patchAnnouncement: (announcementId: string, body: AnnouncementPatch) =>
    apiRequest<AnnouncementOut>(`/admin/announcements/${encodeURIComponent(announcementId)}`, {
      method: "PATCH",
      body,
    }),
  deleteAnnouncement: (announcementId: string) =>
    apiRequest<{ deleted: boolean }>(`/admin/announcements/${encodeURIComponent(announcementId)}`, {
      method: "DELETE",
    }),

  // ---- 审计导出（CSV 附件，上限 5 万行）----
  exportAuditLogs: (query?: { tenant_id?: string; action?: string; entity?: string }) =>
    apiBlob("/admin/audit-logs/export", { query: query as Record<string, QueryValue> }),

  // ---- 租户成员管理 ----
  listMembers: (tenantId: string) =>
    apiRequest<TenantMemberOut[]>(`/admin/tenants/${encodeURIComponent(tenantId)}/members`),
  addMember: (tenantId: string, body: TenantMemberAdd) =>
    apiRequest<TenantMemberOut>(`/admin/tenants/${encodeURIComponent(tenantId)}/members`, {
      method: "POST",
      body,
    }),
  patchMember: (tenantId: string, userId: string, body: TenantMemberPatch) =>
    apiRequest<TenantMemberOut>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
      { method: "PATCH", body },
    ),
  removeMember: (tenantId: string, userId: string) =>
    apiRequest<{ deleted: boolean }>(
      `/admin/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    ),

  // ---- 跨租户数据浏览（只读）----
  dataStores: (query?: { tenant_id?: string; q?: string; page?: number; page_size?: number }) =>
    apiRequest<Page<AdminStoreOut>>("/admin/data/stores", {
      query: query as Record<string, QueryValue>,
    }),
  dataProducts: (query?: {
    tenant_id?: string;
    store_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }) =>
    apiRequest<Page<AdminProductOut>>("/admin/data/products", {
      query: query as Record<string, QueryValue>,
    }),
  dataOrders: (query?: {
    tenant_id?: string;
    store_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }) =>
    apiRequest<Page<AdminOrderOut>>("/admin/data/orders", {
      query: query as Record<string, QueryValue>,
    }),
  dataCustomers: (query?: {
    tenant_id?: string;
    store_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }) =>
    apiRequest<Page<AdminCustomerOut>>("/admin/data/customers", {
      query: query as Record<string, QueryValue>,
    }),
};
